import { RefObject, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { MediaGallery, Text, Toast, Tooltip } from '@belong/ui';
import {
  MaintenanceCostItem,
  MaintenanceMediaModel,
  MaintenanceModel,
} from 'api/models';
import clsx from 'clsx';
import Money from 'components/Money/Money';
import NameValueTable from 'components/NameValueTable/NameValueTable';
import { MaintenanceAccountResponsibility } from 'containercomponents/MaintenanceStatus/MaintenanceAccountResponsibility/MaintenanceAccountResponsibility';
import { SubmitValues } from 'containercomponents/MaintenanceStatus/MaintenanceAccountResponsibility/maintenance-account-responsibility.form';
import Space from 'corecomponents/Space/Space';
import { useRequestAccountResponsibility } from 'maintenance/hooks/useRequestAccountResponsibility';
import { getCostLabel } from 'pages/PostInspectionFlow/steps/Improvements/Approval/Bundle/costs.utils';
import { updateMaintenanceConsent } from 'store/redux/maintenance/actions';
import { splitCamelCase } from 'utils/strings';
import { maintenanceCostStrings } from './maintenance-cost.constants';
import { useMaintenanceCostDisplayData } from './use-maintenance-cost-display-data';

export type MaintenanceCostProps = {
  task: MaintenanceModel;
  taskOptionNumber?: number;
  userRole?: 'Homeowner' | 'Resident';
  onConsentChange: () => Promise<void>;
  accountResponsibilityRef?: RefObject<HTMLDivElement>;
  showScheduleCall?: boolean;
  onScheduleHomecareSpecialistCall?: () => void;
  showItemPrices?: boolean;
  showQuoteImages?: boolean;
  showQuoteApprovalSection?: boolean;
  paddingHorizontal?: boolean;
  roundedTop?: boolean;
};

type FormattedCostItem = {
  type: MaintenanceCostItem['type'];
  description: string;
  cost: number;
};

export const MaintenanceCost = ({
  task,
  taskOptionNumber,
  userRole = 'Homeowner',
  onConsentChange,
  accountResponsibilityRef,
  showScheduleCall,
  onScheduleHomecareSpecialistCall,
  showItemPrices = true,
  showQuoteImages = true,
  showQuoteApprovalSection = true,
  paddingHorizontal = true,
  roundedTop = false,
}: MaintenanceCostProps) => {
  const [isUpdateConsentSuccess, setIsUpdateConsentSuccess] = useState(false);
  const updateMaintenanceConsentMessage = useRef(
    maintenanceCostStrings.responsibilityConsentFail,
  );
  const { financedPayment, isHandledBySmsAssist } = task;
  const {
    availableCost,
    displayResponsibleCost,
    displayResponsiblePercentage,
    otherResponsibleCost,
    otherResponsiblePercentage,
    additionalFees,
    showUpfront,
    quoteImages,
    costItems,
  } = useMaintenanceCostDisplayData({ task, userRole });

  // `costItems` is the hook's `availableCost?.items?.filter(...)`, which this has
  // always dereferenced directly; the filter it comes from keeps only items that
  // have a `description`.
  const formattedCostItems = costItems!.reduce((acc, item) => {
    const existingCostIndex = acc.findIndex((cost) => cost.type === item.type);
    if (existingCostIndex !== -1) {
      const existingCost = acc[existingCostIndex]!;
      acc[existingCostIndex] = {
        ...existingCost,
        cost: existingCost.cost + item.cost,
        description: `${existingCost.description}\n${item.description}`,
      };
    } else {
      acc.push({
        type: item.type,
        description: item.description!,
        cost: item.cost,
      });
    }

    return acc;
  }, [] as FormattedCostItem[]);

  const repairRequestedByResident =
    task.requestType === 'Repair' &&
    task.requestedBy === 'Resident' &&
    !task.doesResidentClaimResponsibility;
  const shouldBeFullyPaidByHomeowner =
    task.requestedBy === 'Homeowner' || repairRequestedByResident;
  const hasPendingQuoteApproval = useRequestAccountResponsibility(
    userRole === 'Resident',
    task,
  );
  const dispatch = useDispatch();

  // TODO: move to MaintenanceStatus on refactor
  const onAccountResponsibilitySubmit = async (values: SubmitValues) => {
    setIsUpdateConsentSuccess(false);
    try {
      updateMaintenanceConsentMessage.current = values.isApproved
        ? maintenanceCostStrings.responsibilityConsentApprovalSuccess
        : showScheduleCall
          ? maintenanceCostStrings.responsibilityConsentRejectionSuccessSchedule
          : maintenanceCostStrings.responsibilityConsentRejectionSuccess;
      await dispatch(updateMaintenanceConsent(task.uniqueId, values));
      setIsUpdateConsentSuccess(true);
      await onConsentChange();
      if (!values.isApproved && onScheduleHomecareSpecialistCall) {
        onScheduleHomecareSpecialistCall();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <div>
        {availableCost && availableCost.items!.length > 0 && (
          <div>
            {!!availableCost.scopeOfWork && (
              <div
                className={clsx(
                  'mb-sm flex flex-col gap-xs',
                  paddingHorizontal && 'mx-sm',
                )}
              >
                <Text className="whitespace-pre-wrap">
                  {availableCost.scopeOfWork}
                </Text>
              </div>
            )}
            {showQuoteImages && quoteImages!.length > 0 && (
              <div className={clsx('mb-sm', paddingHorizontal && 'mx-sm')}>
                {/* @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56 */}
                <MediaGallery<MaintenanceMediaModel> media={quoteImages} />
              </div>
            )}
            <div
              className={clsx('relative mb-lg', paddingHorizontal && 'mx-sm')}
            >
              {task.requestCategory !== 'InHomeRepairs' && (
                <>
                  <NameValueTable
                    responsiveText={false}
                    boldValue
                    items={[
                      {
                        name: maintenanceCostStrings.tableItem,
                        value: maintenanceCostStrings.tableAmount,
                      },
                    ]}
                  />
                  <div className="my-sm border-0 border-b border-dashed border-b-gray" />
                </>
              )}
              <NameValueTable
                responsiveText={false}
                noBold
                items={formattedCostItems.map(({ cost, type, description }) => {
                  let title = splitCamelCase(type);
                  let desc = description;
                  if (isHandledBySmsAssist || type === 'VendorFee') {
                    const descriptionParts = description.split('-');
                    // `split` always yields at least one element.
                    title = descriptionParts.shift()!.trim();
                    desc = descriptionParts.join('-').trim();
                  }
                  return {
                    name: desc,
                    value: showItemPrices ? <Money cash={cost} /> : undefined,
                    title,
                  };
                })}
              />

              {additionalFees!.length > 0 && !isHandledBySmsAssist && (
                <div>
                  <Text fontWeight="semibold">Additional Fees</Text>
                  <div className="mt-xs flex flex-col gap-xs pl-sm">
                    {additionalFees!.map((fee) => (
                      <div key={fee.type}>
                        <Text>{getCostLabel(fee.type)}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="my-sm border-0 border-b border-dashed border-b-gray" />
              <NameValueTable
                responsiveText={false}
                boldValue
                items={[
                  {
                    name: task.actualCost ? (
                      <MaintenanceCostTotalTitle
                        isInHomeRepair={
                          task.requestCategory === 'InHomeRepairs'
                        }
                      />
                    ) : (
                      maintenanceCostStrings.tableEstTotal
                    ),
                    value: <Money cash={availableCost.totalCost} />,
                  },
                ]}
              />

              {otherResponsiblePercentage && (
                <NameValueTable
                  responsiveText={false}
                  boldValue
                  items={[
                    {
                      name: `${
                        userRole === 'Resident'
                          ? maintenanceCostStrings.tableUserRoleHomeowner
                          : maintenanceCostStrings.tableUserRoleResident
                      } Responsibility (${otherResponsiblePercentage}%)`,
                      value: <Money cash={otherResponsibleCost} />,
                    },
                  ]}
                />
              )}
              {!!displayResponsiblePercentage &&
                displayResponsiblePercentage < 100 && (
                  <NameValueTable
                    responsiveText={false}
                    boldValue
                    items={[
                      {
                        name: `${maintenanceCostStrings.tableResponsiblePercentage} (${displayResponsiblePercentage}%)`,
                        value: <Money cash={displayResponsibleCost} />,
                      },
                    ]}
                  />
                )}
              {!displayResponsiblePercentage && (
                <NameValueTable
                  responsiveText={false}
                  boldValue
                  items={[
                    {
                      name: maintenanceCostStrings.tableResponsiblePercentageZero,
                      value:
                        maintenanceCostStrings.tableResponsiblePercentageZeroValue,
                    },
                  ]}
                />
              )}
              {showUpfront && (
                <NameValueTable
                  responsiveText={false}
                  boldValue
                  items={[
                    {
                      name: maintenanceCostStrings.tableDueUpfront,
                      value: <Money cash={financedPayment?.dueNow} />,
                    },
                  ]}
                />
              )}
            </div>

            {hasPendingQuoteApproval && showQuoteApprovalSection && (
              <div ref={accountResponsibilityRef}>
                <MaintenanceAccountResponsibility
                  onSubmit={onAccountResponsibilitySubmit}
                  showScheduleCall={showScheduleCall}
                  shouldShowSharedExpenseOption={!shouldBeFullyPaidByHomeowner}
                  taskOptionNumber={taskOptionNumber}
                  roundedTop={roundedTop}
                />
              </div>
            )}
          </div>
        )}
        <Toast
          autocloseDuration={5000}
          isVisible={isUpdateConsentSuccess}
          onClose={() => setIsUpdateConsentSuccess(false)}
        >
          {updateMaintenanceConsentMessage.current}
        </Toast>
      </div>
    </>
  );
};

const MaintenanceCostTotalTitle = ({
  isInHomeRepair,
}: {
  isInHomeRepair: boolean;
}) => (
  <div className="flex gap-2xs">
    <Text fontWeight="semibold">{maintenanceCostStrings.tableTotal}</Text>
    {!isInHomeRepair && (
      <Tooltip
        arrowProps={{ className: 'fill-dark-navy' }}
        contentProps={{
          side: 'top',
          alignOffset: -48,
          align: 'start',
          sideOffset: 8,
        }}
        content={
          <div className="max-w-xl rounded bg-dark-navy p-2sm text-white sm:max-w-4xl">
            <Text className="whitespace-pre-wrap" fontWeight="semibold">
              {maintenanceCostStrings.tableTooltipTitle}
            </Text>
            <Space />
            <Text fontWeight="semibold">
              {maintenanceCostStrings.tableTooltipListTitle}
            </Text>
            <ul className="list-decimal pl-md">
              {maintenanceCostStrings.tableTooltipListItems.map(
                (item: string, index: number) => (
                  <li key={index}>
                    <Text>{item}</Text>
                  </li>
                ),
              )}
            </ul>
          </div>
        }
      />
    )}
  </div>
);
